import React, { useEffect, useRef, useState } from 'react';

/**
 * CursorTrail — customizable trail behind the mouse. The old file was named
 * HexagonCursorTrail; this component supports multiple shapes/colors/sizes
 * while keeping the same public prop shape (backward compatible).
 *
 *   shape:     'hexagon' | 'bullet_hole' | 'dot' | 'star'
 *   color:     'silver' | 'red' | 'gold' | 'green'
 *   size:      'small' | 'medium' | 'large'
 *   intensity: 'subtle' | 'medium' | 'bold'   — density / count
 *
 * Never blocks clicks (pointer-events: none). Auto-disabled on touch devices
 * and when the visitor prefers reduced motion.
 */

const INTENSITY_PRESETS = {
  subtle: { maxTrail: 8,  spacing: 40, fadeMs: 400 },
  medium: { maxTrail: 14, spacing: 30, fadeMs: 550 },
  bold:   { maxTrail: 22, spacing: 22, fadeMs: 700 },
};

const SIZE_PX = { small: 9, medium: 13, large: 18 };

const COLORS = {
  silver: 'rgba(148, 163, 184, 0.60)',
  red:    'rgba(239, 68, 68, 0.70)',
  gold:   'rgba(234, 179, 8, 0.70)',
  green:  'rgba(34, 197, 94, 0.65)',
};

const SHAPES = {
  hexagon: ({ stroke }) => (
    <polygon points="12,2 22,7 22,17 12,22 2,17 2,7" fill="none" stroke={stroke} strokeWidth="1.6" />
  ),
  bullet_hole: ({ stroke }) => (
    <>
      <circle cx="12" cy="12" r="9" fill="none" stroke={stroke} strokeWidth="1.4" />
      <circle cx="12" cy="12" r="4.5" fill={stroke} opacity="0.55" />
    </>
  ),
  dot: ({ stroke }) => (
    <circle cx="12" cy="12" r="6" fill={stroke} />
  ),
  star: ({ stroke }) => (
    <polygon
      points="12,2 14.6,9 22,9.5 16.2,14.3 18.2,21.5 12,17.6 5.8,21.5 7.8,14.3 2,9.5 9.4,9"
      fill="none" stroke={stroke} strokeWidth="1.4"
    />
  ),
};

const CursorTrail = ({
  style,                    // legacy prop from earlier version (maps to intensity)
  shape = 'hexagon',
  color = 'silver',
  size = 'medium',
  intensity,
}) => {
  const chosenIntensity = intensity || style || 'subtle';
  const preset = INTENSITY_PRESETS[chosenIntensity] || INTENSITY_PRESETS.subtle;
  const px = SIZE_PX[size] || SIZE_PX.medium;
  const stroke = COLORS[color] || COLORS.silver;
  const Shape = SHAPES[shape] || SHAPES.hexagon;

  const [trail, setTrail] = useState([]);
  const lastPointRef = useRef({ x: -9999, y: -9999 });
  const idRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const isTouch = window.matchMedia?.('(hover: none)').matches;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (isTouch || reduced) return undefined;

    const handleMove = (e) => {
      const { x: lx, y: ly } = lastPointRef.current;
      const dx = e.clientX - lx;
      const dy = e.clientY - ly;
      if ((dx * dx + dy * dy) < preset.spacing * preset.spacing) return;
      lastPointRef.current = { x: e.clientX, y: e.clientY };
      idRef.current += 1;
      const id = idRef.current;
      setTrail((prev) => {
        const next = [...prev, { id, x: e.clientX, y: e.clientY }];
        return next.length > preset.maxTrail ? next.slice(-preset.maxTrail) : next;
      });
      setTimeout(() => {
        setTrail((prev) => prev.filter((h) => h.id !== id));
      }, preset.fadeMs);
    };

    window.addEventListener('mousemove', handleMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMove);
  }, [preset.spacing, preset.maxTrail, preset.fadeMs]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[9998]" aria-hidden="true" data-testid="cursor-trail">
      {trail.map((h) => (
        <svg
          key={h.id}
          width={px}
          height={px}
          viewBox="0 0 24 24"
          style={{
            position: 'absolute',
            left: h.x - px / 2,
            top: h.y - px / 2,
            animation: `cursorTrailFade ${preset.fadeMs}ms ease-out forwards`,
          }}
        >
          <Shape stroke={stroke} />
        </svg>
      ))}
      <style>{`
        @keyframes cursorTrailFade {
          from { opacity: 0.9; transform: scale(1); }
          to   { opacity: 0;   transform: scale(0.55); }
        }
      `}</style>
    </div>
  );
};

export default CursorTrail;
